import { useIsDiarisationUiEnabled } from '@/features/flags/diarisation-ui-flag';
import { track } from '@/lib/analytics/track';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import {
  t11nClient,
  TranscriptResponseSchema,
  type TranscriptResponse,
} from '@/lib/SessionRecordingProvider/t11nClient';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import {
  mergeTranscriptSnapshots,
  notFoundSessionIds,
  stripDiarisedTranscriptFromResponse,
  updateTranscriptCache,
} from '@heidi/session/data';
import { useQuery } from '@tanstack/react-query';
import { MAX_QUERY_RETRIES } from './constants';

// Definition moved to `@heidi/session/contracts` (ARCFE-983). import +
// re-export: this module uses the name locally, and a bare
// `export { X } from '...'` does not bind it here.
import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '@heidi/session/contracts';

export { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY };

/**
 * Update the cached t11n transcript, seeding an empty transcript when no cache
 * entry exists. All cache writers must use this — bailing on a missing entry
 * silently drops transcript data.
 */
export const updateT11nTranscriptCache = (
  sessionId: string,
  updater: (state: TranscriptResponse) => TranscriptResponse
): void => {
  updateTranscriptCache(sessionId, updater, { session_id: sessionId, recordings: [] });
};

/** Do not retry on session-not-found 404 errors. */
export const shouldRetryUnlessSessionNotFound = (failureCount: number, error: Error) => {
  if (isSessionNotFoundError(error)) {
    return false;
  }

  return failureCount < MAX_QUERY_RETRIES;
};

const diarisedPayloadLoggedSessionIds = new Set<string>();
const transcriptSchemaCheckedSessionIds = new Set<string>();
export const resetDiarisationLogLatchesForTests = (): void => {
  diarisedPayloadLoggedSessionIds.clear();
  transcriptSchemaCheckedSessionIds.clear();
};

type DiarisedPayloadOutcome = 'full' | 'partial' | 'none';

// Counts only — never labels or text. Speaker count is distinct
// `segment.speaker_id` across every chunk (what the pane actually renders).
const summariseDiarisedPayload = (recordings: TranscriptResponse['recordings']) => {
  const chunks = recordings.flatMap((recording) => recording.chunks);
  const speakerIds = new Set<string>();
  let chunksWithSegments = 0;
  for (const chunk of chunks) {
    const segments = chunk.diarised_transcript?.segments ?? [];
    if (segments.length > 0) chunksWithSegments += 1;
    for (const segment of segments) speakerIds.add(segment.speaker_id);
  }
  const outcome: DiarisedPayloadOutcome =
    chunksWithSegments === 0
      ? 'none'
      : chunksWithSegments < chunks.length
        ? 'partial'
        : 'full';
  return {
    outcome,
    recording_count: recordings.length,
    chunk_count: chunks.length,
    chunks_with_segments: chunksWithSegments,
    speaker_count: speakerIds.size,
  };
};

/**
 * Transcription-service (t11n) transcript for a session. Sole owner of the
 * query key — never mount an inline `useQuery` on it.
 *
 * `settled` is whether the caller believes the recording has stopped. The
 * diarised-outcome telemetry only fires on a settled read — see the queryFn.
 */
export function useQueryT11nTranscriptBySessionId(
  sessionId: string,
  {
    enabled = true,
    diarise = false,
    settled = true,
  }: { enabled?: boolean; diarise?: boolean; settled?: boolean } = {}
) {
  const requestDiarised = diarise === true;
  const uiEnabled = useIsDiarisationUiEnabled();
  const surface = isTauri() ? 'desktop' : 'web';

  return useQuery({
    // Diarised fetches live in their own cache entry so the shared bare
    // transcript fetch (timer, uploads) can never clobber the diarised
    // payload. Prefix invalidation on `[key, sessionId]` still matches both.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the key encodes `requestDiarised` by construction (bare tuple vs `{ diarise: true }` scope)
    queryKey: requestDiarised
      ? [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId, { diarise: true }]
      : [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId],
    enabled: enabled && !!sessionId && !notFoundSessionIds.has(sessionId),
    retry: shouldRetryUnlessSessionNotFound,
    queryFn: async () => {
      const response = await t11nClient.get<TranscriptResponse>(
        `/${sessionId}/transcript`,
        requestDiarised ? { params: { diarise: true } } : undefined
      );
      const recordings = response.data.recordings ?? [];
      if (!transcriptSchemaCheckedSessionIds.has(sessionId)) {
        transcriptSchemaCheckedSessionIds.add(sessionId);
        const parsed = TranscriptResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          logger.warn('[DIARISATION] transcript payload failed schema', {
            session_id: sessionId,
            issue_count: parsed.error.issues.length,
            first_path: parsed.error.issues[0]?.path.join('.') ?? '',
          });
        }
      }
      // Settled reads only. The query is live while the timer runs, and a
      // mid-recording payload legitimately trails diarisation, so latching on
      // the first chunked fetch would pin `outcome` to none/partial for the
      // session. Skip without latching. The next queryFn after stop — a remount
      // (`refetchOnMount: 'always'`) or an invalidation that runs while
      // settled is true — emits the settled shape. Window-focus will not
      // refetch: the client staleTime is 1h.
      if (
        requestDiarised &&
        settled &&
        recordings.length > 0 &&
        !diarisedPayloadLoggedSessionIds.has(sessionId)
      ) {
        const summary = summariseDiarisedPayload(recordings);
        if (summary.chunk_count > 0) {
          diarisedPayloadLoggedSessionIds.add(sessionId);
          logger.info('[DIARISATION] diarised payload settled', {
            session_id: sessionId,
            ...summary,
          });
          track({
            event_name: TrackingEventName.DiarisedTranscriptViewed,
            props: {
              session_id: sessionId,
              outcome: summary.outcome,
              surface,
              speaker_count: summary.speaker_count,
              chunk_count: summary.chunk_count,
              recording_count: summary.recording_count,
              ui_enabled: uiEnabled,
            },
            options: { amplitude: false },
          });
        }
      }
      const payload = { ...response.data, recordings };
      return requestDiarised ? payload : stripDiarisedTranscriptFromResponse(payload);
    },
    refetchOnMount: 'always',
    refetchInterval: false,
    structuralSharing: (oldData, newData) => {
      const merged = mergeTranscriptSnapshots(
        oldData as TranscriptResponse | undefined,
        newData as TranscriptResponse | undefined
      );
      if (!merged || requestDiarised) {
        return merged;
      }
      return stripDiarisedTranscriptFromResponse(merged);
    },
  });
}
